import { reactive, toRefs, computed } from 'vue';
import { gql } from '@apollo/client/core';
import { formatPrice } from '@/components/commons/MoneyUtils.js';
import { trackEvent } from '@/components/commons/utils.js';
import { executeQuery } from '@/components/commons/apolloUtils';
import Tracking from '../components/commons/Tracking.js';
import CountryUtils from '@/components/commons/CountryUtils';
import LocationService from '@/services/LocationService';
import { formatMoney } from '@/components/cart/utils';
import md5 from "blueimp-md5";
import UserService from "@/services/UserService";

const PRODUCT_PAGE_QUERY = gql`
  query PRODUCT($productHashKey: ID!) {
    product(hashKey: $productHashKey) {
      hashKey
      baseHashKey
      activeVersionHashKey
      alternativeHashKeys
      slug
      status
      active
      skuNo
      title
      country
      imageUrl
      description
      marketingTitle
      marketingDescription
      metaTitle
      metaDescription
      fabricAndStain
      packagedItemQuantity
      inStock
      isEtaAtRisk
      onSale
      onClearance
      isNewProduct
      configuratorEnabled
      collection {
        id
        name
        slug
        productCount
        productThumbnail {
          hashKey
          skuNo
          title
          slug
          imageUrl
          onSale
          onClearance
          price {
            currencyCode
            sale
            price
            retail
            clearance
          }
        }
      }
      review {
        count
        rating
      }
      taxonomyCategory {
        id
        name
        pixleeAlbumId
      }
      navigationCategory {
        id
        name
        displayName
        slug
        parentId
        parentName
        parentDisplayName
        parentSlug
      }
      price {
        currencyCode
        sale
        price
        retail
        clearance
      }
      care
      spec {
        key
        value
      }
      feature
      media {
        type
        url
        thumbnailUrl
        dashUrl
        hlsUrl
      }
      related {
        hashKey
        skuNo
        title
        slug
        imageUrl
        onSale
        onClearance
        price {
          currencyCode
          sale
          price
          retail
          clearance
        }
      }
      variation {
        hashKey
        fabricAndStain
        popularity
        variationImageUrl
        title
        slug
      }
      similarSku {
        hashKey
        skuNo
      }
      taxonomyAttribute {
        attributeId
        name
        type
        configuratorAttribute
        valueId
        value
        imageUrl
      }
      configurationGroup {
        attributeId
        name
        type
        configuration {
          hashKey
          valueId
          value
          imageUrl
          selected
        }
      }
      box {
        height
        width
        depth
      }
      height
      width
      depth
      assemblyInstructionsUrl
    }
  }
`;


const PRODUCTS_QUERY = gql`
  query PRODUCTS($productHashKeys: [ID]!) {
    products(hashKeys: $productHashKeys) {
      hashKey
      slug
      status
      active
      skuNo
      title
      country
      imageUrl
      onSale
      onClearance
      price {
        currencyCode
        sale
        price
        retail
        clearance
      }
    }
  }
`;


const currencyCode = LocationService.isCanada() ? 'CAD' : 'USD';

function formatProduct(product) {
  const formattedProduct = { ...product };

  if (!product.formatted) {
    formattedProduct.imageUrl = product.imageUrl ? `${product.imageUrl}?fit=max&w={width}&q={q}` : null;
    formattedProduct.variationImageUrl = product.variationImageUrl ? `${product.variationImageUrl}?fit=max&w={width}&q={q}` : null;

    const price = product.price?.find((priceMap) => priceMap.currencyCode === currencyCode);
    if (price) {
      formattedProduct.originalPrice = formatMoney(CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol, price.price);
      formattedProduct.priceCents =
        product.onSale || product.onClearance
          ? Math.min(...[price.clearance, price.sale, price.price].filter(Boolean))
          : Math.min(...[price.clearance, price.price].filter(Boolean));
      formattedProduct.price = formatMoney(
        CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol,
        formattedProduct.priceCents,
      );
    }
    formattedProduct.formatted = true;
  }

  return formattedProduct;
}

export async function fetchProductsByIds(ids){
  try {
    const result = await executeQuery(PRODUCTS_QUERY, {
      productHashKeys: ids,
    });
    return result.products.map(formatProduct)
  } catch (error) {
    Tracking.sentryCaptureError(error);
    return {
      error: `Failed to load products with ids ${ids.join(",")}`,
    };
  }
}

async function load(id) {
  try {
    const result = await executeQuery(PRODUCT_PAGE_QUERY, {
      productHashKey: id,
    });
    return JSON.parse(JSON.stringify(result));
  } catch (error) {
    Tracking.sentryCaptureError(error);
    return {
      error: `Failed to load product with id ${id}`,
    };
  }
}

const PAIR = 2;

export default function useProduct({ forcePair } = { forcePair: false }) {
  
  const state = reactive({
    product: null,
    price: null,
    forcePair,
    error: null,
    loading: false,
    showUgc: true,
    inStockIn: null,
    currentOpenConfigurator: null,
    showHeadCart: false,
    hideTopInfo: false,
  });

  const currentNavigationCategoryPath = computed(() => {
    const category = currentNavigationCategory.value;
    if (category) {
      return [category.parentName, category.name].filter((name) => name).join('/');
    }
    return '';
  });

  function startLoading() {
    state.loading = true;
  };

  function finishLoading() {
    state.loading = false;
  };

  function updateShowUgc(show) {
    state.showUgc = show;
  }

  function updateSelectedConfigurations(configuration) {
    const configurationGroup = state.product?.configurationGroup?.find((cg) => cg.attributeId === configuration.configurationGroup.attributeId);
    if (configurationGroup) {
      configurationGroup.configuration.forEach((config) => (config.selected = config.valueId === configuration.value.valueId));
    }
  };

  async function loadConfiguration(configuration) {
    if (!configuration.value || !configuration.configurationGroup || !configuration.value.hashKey) {
      return;
    }

    updateSelectedConfigurations(configuration);

    startLoading();
    const result = await load(configuration.value.hashKey);
    state.error = result.error;
    const country = CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId());
    state.product = result.product;
    if (result.product) {
      state.product.isProductAvailableInGeo = country && result.product.country ? result.product.country.includes(country.code) : true;
      state.price = formatPrice(state.product, CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()), state.forcePair && state.product.packagedItemQuantity === PAIR ? PAIR : 1);
    }
    finishLoading();
    Tracking.track('PDP Configuration Changed', {
      productId: configuration.value.hashKey,
      attribute: configuration.configurationGroup.name,
      attributeId: configuration.configurationGroup.attributeId,
      value: configuration.value.value,
      valueId: configuration.value.valueId
    });
  };

  function trackAddToCart() {

    const eventId = md5(UserService.getSessionId() + '_' + state.product.skuNo); 

    trackEvent('Products', 'Add To Cart', state.product.skuNo, state.price.final / 100, {
      name: state.product.title,
      id: state.product.skuNo,
      price: state.price.final / 100,
      currencyCode: state.price.currency,
      category: currentNavigationCategoryPath.value,
      hashedId: eventId,
    });
    Tracking.track('Add To Cart', {
      productId: state.product.hashKey,
      skuNo: state.product.skuNo,
      price: state.price.final / 100,
      name: state.product.title,
      currencyCode: state.price.currency,
      category: currentNavigationCategoryPath.value,
    });
  }

  function trackProductView() {
    try {
      const eventId = md5(UserService.getSessionId() + state.product.skuNo);
      trackEvent('Product', 'Product Viewed', `${state.product.skuNo} - ${state.product.title}`, state.price.final / 100, {
        currencyCode: state.price.currency,
        name: state.product.title,
        id: state.product.skuNo,
        price: state.price.final / 100,
        category: currentNavigationCategoryPath.value,
        hashedId: eventId,
      });
      Tracking.track("VIEW_CONTENT", {
        currencyCode: state.price.currency,
        name: state.product.title,
        id: state.product.skuNo,
        price: state.price.final / 100,
        category: currentNavigationCategoryPath.value,
        email: UserService.getEmail(),
        productId: state.product.hashKey,
      });

      if ('ga' in window) {
        ga('set', 'page', window.location.pathname);
        ga('send', 'pageview');
      }

      // TODO: reenable
      // const { trackProductClick } = useFilterProduct();
      // trackProductClick(state.product.hashKey);
    } catch (e) {
      console.warn(e);
    }
  }

  const configurations = computed(() => {
    if (!state.product || !state.product.configurationGroup || !state.product.configurationGroup.length) {
      if (state.product && state.product.variation && state.product.variation.length > 1) {
        return [
          {
            attributeId: -1,
            name: 'Color',
            type: 'CIRCLE',
            configuration: state.product.variation.map((variation, index) => ({
              hashKey: variation.hashKey,
              valueId: index,
              value: variation.fabricAndStain,
              imageUrl: variation.variationImageUrl,
              selected: state.product.hashKey === variation.hashKey,
            })),
          },
        ];
      }
      return [];
    }

    return state.product.configurationGroup.filter((group) => group.configuration.length > 1) || [];
  });

  const lastImage = computed(() => {
    return state.product && state.product.media ? [...state.product.media].reverse().find((media) => media.type === 'IMAGE' || !media.type) : null;
  });

  const currentNavigationCategory = computed(() => {
    return state.product?.navigationCategory?.[0] || {};
  });

  const reviewUrl = computed(() => {
    if (state.product) {
      const url = encodeURIComponent(`/reviews/edit/product?p=${state.product.skuNo}&productId=${state.product.hashKey}`);
      return `/login?next=${url}`;
    }

    return '';
  });

  return {
    ...toRefs(state),
    load: async (id) => {
      const result = await load(id);
      state.error = result.error;
      const country = CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId());
      state.product = result.product;
      if (result.product) {
        state.price = formatPrice(state.product, CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()), state.forcePair && result.product.packagedItemQuantity === PAIR ? PAIR : 1);
        state.product.isProductAvailableInGeo = country && result.product.country ? result.product.country.includes(country.code) : true;
      }
    },
    loadConfiguration,
    formatProduct,
    configurations,
    lastImage,
    updateShowUgc,
    currentNavigationCategory,
    currentNavigationCategoryPath,
    reviewUrl,
    trackAddToCart,
    trackProductView
  };
}
